import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as UploadIcon } from "../../../assets/images/upload.svg";
import { buildAndDownloadCsv } from "../../../helper/csvHelper";
import { validateFile } from "../../../helper/files";
import { sortByPtBR } from "../../../helper/sortHelper";
import { addInvestors, getEmployees, updateEmployee } from "../../../redux/actions/employees";
import { inviteEmployee } from "../../../redux/actions/invites";
import { getManagers } from "../../../redux/actions/managers";
import { getCompanyStructure } from "../../../redux/actions/structure";
import DragAndDropUpload from "../../global/form/DragAndDropUpload/DragAndDropUpload";
import UploadButton from "../../global/form/UploadButton/UploadButton";
import showNotification from "../../global/Notification/showNotification";
import PopUpCheck from "../../global/PopUpCheck/PopUpCheck";
import SaveModal from "../../global/SaveModal/SaveModal";
import "./InviteModalCollaborators.scss";

const InviteModalCollaborators = ({ onClose, employee }) => {
  const dispatch = useDispatch();
  const [selectedRole, setSelectedRole] = useState("");
  const structure = useSelector((state) => state?.structure);
  const managers = useSelector(state => state.managers);
  const [units, setUnits] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedArea, setSelectedArea] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [popUpActive, setPopUpActive] = useState(false);
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({});
  const [initialInfo, setInitialInfo] = useState({ error: false });

  const [isModalOpenRole, setIsModalOpenRole] = useState(false);
  const [isModalOpenUnit, setIsModalOpenUnit] = useState(false);

  const [managerSearch, setManagerSearch] = useState("");
  const [showManagerDropdown, setShowManagerDropdown] = useState(false);
  const [selectedManager, setSelectedManager] = useState(null);
  const managerInputRef = useRef(null);
  const managerDropdownRef = useRef(null);
  useEffect(() => {
    if (!showManagerDropdown) return;
    function handleClickOutside(event) {
      if (
        managerInputRef.current &&
        !managerInputRef.current.contains(event.target) &&
        managerDropdownRef.current &&
        !managerDropdownRef.current.contains(event.target)
      ) {
        setShowManagerDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showManagerDropdown]);

  useEffect(() => {
    if (!showManagerDropdown || !selectedArea) return;
    const trimmed = managerSearch.trim();
    const params = trimmed ? { name: trimmed } : {};
    const handle = setTimeout(() => dispatch(getManagers(params)), 250);
    return () => clearTimeout(handle);
  }, [managerSearch, showManagerDropdown, selectedArea, dispatch]);

  const handleManagerInputChange = (display, id) => {
    setSelectedManager({ id, display });
    setManagerSearch("");
    setShowManagerDropdown(false);
  };

  const handleAddUnit = (newArea) => {
    setUnits(sortByPtBR([...units, newArea]));
    setSelectedArea(newArea);
  };

  const handleAddRole = (newArea) => {
    setRoles(sortByPtBR([...roles, newArea]));
    setSelectedRole(newArea);
  };

  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);

  const handleFileUpload = (e) => {
    const file = (e.target && e.target.files[0]) || e;

    if (validateFile(file)) {
      setError(false)
      setFile(file);
      const reader = new FileReader();
      reader.readAsDataURL(file);
    } else {
      setError(true);
    }
  };

  useEffect(() => {
    if (employee) {
      setEmail(employee?.person?.email);
      setSelectedArea(employee?.unit);
      setSelectedRole(employee?.role);
      if (employee.manager) {
        const name = employee.manager?.name;
        const email = employee.manager?.email;
        const display = name && email ? `${name} - ${email}` : name || employee.manager?.email || String(employee.manager?.id);
        setSelectedManager({ id: employee.manager.id, display });
      } else {
        setSelectedManager(null);
      }
    }
  }, [employee]);

  useEffect(() => {
    dispatch(getCompanyStructure());
  }, [dispatch]);

  useEffect(() => {
    if (selectedArea && Array.isArray(structure?.data)) {
      if (!employee) setSelectedRole("");
      const roles = structure?.data
        .map((unit) => (unit.name === selectedArea ? unit.roles : null))
        .filter(Boolean)
        .map((role) => role.map((item) => (item)))[0];
      setRoles(sortByPtBR(roles ?? []));
    }
  }, [employee, selectedArea, structure]);


  useEffect(() => {
    if (Array.isArray(structure?.data)) {
      setUnits(sortByPtBR(structure?.data?.map((unit) => (unit.name))));
    }
  }, [structure?.data]);

  const handleChangeUnit = (event) => {
    const value = event.target.value;
    setSelectedArea(value);
    setShowManagerDropdown(false);
    setManagerSearch("");
    setSelectedManager(null);
    if (value) {
      setErrors((prev) => ({ ...prev, selectedArea: null }));
    }
  };

  const handleChangeRole = (event) => {
    const value = event.target.value;
    setSelectedRole(value);
    if (value) {
      setErrors((prev) => ({ ...prev, selectedRole: null }));
    }
  };

  const handleEmployeesCsvSubmission = () => {
    setIsLoading(true);
    const payload = {
      file
    };
    dispatch(addInvestors(payload))
      .then(() => {
        setPopUpActive(true);
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        if (error?.message === "Manager not found or does not have access to this company") {
          showNotification("Gestor não encontrado ou não tem acesso a esta empresa", 5000);
        } else {
          showNotification("Erro ao processar arquivo CSV. Tente novamente.", 5000);
        }
      });
  }

  const handleInviteEmployee = () => {
    setIsLoading(true);

    const payload = {
      email,
      unit: selectedArea,
      role: selectedRole,
      managerId: selectedManager?.id,
    };

    dispatch(inviteEmployee(payload)).then(() => {
      setIsLoading(false);
      dispatch(getEmployees());
      setPopUpActive(true);
      showNotification("Convite enviado com sucesso!", 5000);
    }).catch((error) => {
      setIsLoading(false);
      if (error?.message === "Manager not found or does not have access to this company") {
        showNotification("Gestor não encontrado ou não tem acesso a esta empresa", 5000);
      } else {
        showNotification("Erro ao enviar convite. Tente novamente.", 5000);
      }
    });
  };

  const handleEditEmployee = () => {
    setIsLoading(true);
    if (selectedRole === employee?.role && selectedArea === employee?.unit && selectedManager?.id === employee?.manager?.id) {
      setInitialInfo({ error: true });
      setPopUpActive(true);
      setIsLoading(false);
      return;
    }

    const payload = {
      unit: selectedArea,
      role: selectedRole,
      managerId: selectedManager?.id,
    };
    dispatch(updateEmployee(payload, employee?.id)).then(() => {
      setIsLoading(false);
      dispatch(getEmployees());
      setPopUpActive(true);
      showNotification("Colaborador editado com sucesso!", 5000);
    }).catch((error) => {
      setIsLoading(false);
      if (error?.message === "Manager not found or does not have access to this company") {
        showNotification("Gestor não encontrado ou não tem acesso a esta empresa", 5000);
      } else {
        showNotification("Erro ao editar colaborador. Tente novamente.", 5000);
      }
    });
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    if (employee) {
      handleEditEmployee();
    } else {
      if (file) {
        handleEmployeesCsvSubmission();
      } else {
        const newErrors = {};
        if (!email) newErrors.email = "E-mail é obrigatório.";
        if (!selectedArea) newErrors.selectedArea = "Selecione uma área.";
        if (!selectedRole) newErrors.selectedRole = "Selecione um cargo.";

        if (Object.keys(newErrors).length > 0) {
          setErrors(newErrors);
        } else {
          handleInviteEmployee();
          setErrors({});
        }
      }
    }
  };

  const isStructureLoading = Boolean(structure?.isFetching);
  const isManagerLoading = Boolean(managers?.isFetching);
  const showModalOverlay = isLoading || (isStructureLoading && units.length === 0);
  const modalLoadingMessage = isLoading ? "Salvando alterações..." : "Carregando opções...";

  return createPortal(
    <div className="InviteModalCollaborators">
      <div className="imc-backdrop" onClick={onClose} />
      <div className={`invite-modal ${employee ? "is-editing" : "is-invite"}`}>
        {showModalOverlay && (
          <div className="modal-loading-overlay" aria-live="polite">
            <div className="modal-loading-spinner" />
            <p>{modalLoadingMessage}</p>
          </div>
        )}
        <button className="close-button" onClick={onClose} disabled={isLoading}>
          FECHAR <span className="material-symbols--close" />
        </button>
        <div className="invite-modal-header">
          {!employee && <h2>Acessos <span className="solar--alt-arrow-right-line-duotone" />Convidar colaboradores</h2>}
          {employee && (
            <>
              <h2>Acessos <span className="solar--alt-arrow-right-line-duotone" />Editar <span className="solar--alt-arrow-right-line-duotone" /> colaborador</h2>
              <p className="employee-email">{email}</p>
            </>
          )}
          {!employee && <p>Aqui, você pode gerenciar os usuários e permissões da plataforma.</p>}
        </div>
        <form className="invite-modal-form" onSubmit={handleSubmit}>
          <div className="container-box-invite">
            <div className={`invite-section`}>
              {!employee && (
                <>
                  <h3>Para quem você quer enviar esse convite?</h3>
                  <div className="inputs-box">
                    <div>
                      <input
                        type="email"
                        placeholder="E-mail"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          if (e.target.value) {
                            setErrors((prev) => ({ ...prev, email: null }));
                          }
                        }}
                      />
                      {errors.email && <span className="error">{errors.email}</span>}
                    </div>
                  </div>
                </>
              )}
              {!employee &&
                <>
                  <h3>Qual será a área e o cargo de acesso?</h3>
                  <div className="inputs-box">
                    <div className="custom-select-container next-button">
                      <div className="select-wrapper">
                        <select
                          className="custom-select"
                          value={selectedArea}
                          onChange={handleChangeUnit}
                          disabled={isStructureLoading}
                        >
                          <option value="" disabled>
                            Selecione a área
                          </option>
                          {units?.map((area, index) => (
                            <option key={index} value={area}>
                              {area}
                            </option>
                          ))}
                        </select>
                        <span className="custom-icon">
                          <span className="weui--arrow-outlined" />
                        </span>
                      </div>
                      <button className="add-button" type="button" onClick={() => setIsModalOpenUnit(true)}>
                        +
                      </button>
                      {errors?.selectedArea && <span className="error">{errors.selectedArea}</span>}
                    </div>
                    <div className="custom-select-container next-button">
                      <div className="select-wrapper">
                        <select
                          className="custom-select"
                          value={selectedRole}
                          onChange={handleChangeRole}
                          disabled={isStructureLoading || !selectedArea}
                        >
                          <option value="" disabled>
                            Selecione o cargo
                          </option>
                          {roles?.map((role, index) => (
                            <option key={index} value={role}>
                              {role}
                            </option>
                          ))}
                        </select>
                        <span className="custom-icon">
                          <span className="weui--arrow-outlined" />
                        </span>
                      </div>
                      <button className="add-button" type="button" onClick={() => setIsModalOpenRole(true)}>
                        +
                      </button>
                      {errors?.selectedRole && <span className="error">{errors.selectedRole}</span>}
                    </div>


                    <div className="custom-select-container next-button gestor-manager-container">
                      <div className="select-wrapper gestor-manager-input" style={{ position: 'relative' }}>
                        <input
                          type="text"
                          id="manager"
                          ref={managerInputRef}
                          placeholder={selectedArea ? "Digite para buscar gestor..." : "Selecione uma área primeiro"}
                          value={selectedManager ? selectedManager.display : managerSearch}
                          onChange={e => {
                            setManagerSearch(e.target.value);
                            setShowManagerDropdown(true);
                            setSelectedManager(null);
                          }}
                          onFocus={() => selectedArea && setShowManagerDropdown(true)}
                          className="custom-select"
                          disabled={!selectedArea}
                          autoComplete="off"
                        />
                        <span className="custom-icon" style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)' }}>
                          <span className="weui--arrow-outlined" />
                        </span>
                        <label htmlFor="manager" className="gestor-label">Gestor Responsável</label>
                        {showManagerDropdown && selectedArea && (
                          <div
                            className="manager-dropdown"
                            ref={managerDropdownRef}
                          >
                            {(() => {
                              if (isManagerLoading && !selectedManager) {
                                return <div className="manager-empty">Buscando gestores...</div>;
                              }
                              const filtered = Array.isArray(managers?.data?.items)
                                ? managers.data.items.filter(item => {
                                  const name = item?.manager?.name;
                                  const email = item?.manager?.email;
                                  const display = name && email ? `${name} - ${email}` : name || email || item?.manager?.email || String(item?.id);
                                  return display && (managerSearch === '' || display.toLowerCase().includes(managerSearch.toLowerCase()));
                                })
                                : [];
                              if (filtered.length === 0) {
                                return <div className="manager-empty">Nenhum gestor encontrado</div>;
                              }
                              return filtered.map(item => {
                                const name = item?.manager?.name;
                                const email = item?.manager?.email;
                                const display = name && email ? `${name} - ${email}` : name || email || item?.manager?.email || String(item?.id);
                                const isSelected = selectedManager?.id === item.manager?.id;
                                return (
                                  <div
                                    key={item.id || item.manager?.email || name}
                                    onClick={() => {
                                      handleManagerInputChange(display, item.manager.id);
                                    }}
                                    className={`manager-option improved ${isSelected ? 'selected' : ''}`}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 8,
                                      padding: '12px 18px',
                                      borderBottom: '1px solid #232a4d',
                                      fontSize: 15,
                                      background: isSelected ? '#232a4d' : 'none',
                                      color: isSelected ? '#ccc' : '#fff',
                                      cursor: isSelected ? 'default' : 'pointer'
                                    }}
                                  >
                                    <span style={{ fontWeight: 500 }}>{display}</span>
                                    {isSelected && <span style={{ marginLeft: 'auto', fontSize: 12 }}>✓</span>}
                                  </div>
                                );
                              });
                            })()}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </>}
            </div>
            <div className={`role-section`}>

              {!employee &&
                <>
                  <h3>Se preferir, adicione o arquivo CSV no padrão E2W com a lista de colaboradores.</h3>
                  <p className="download-csv">
                    <span onClick={() => buildAndDownloadCsv(
                      "nome;email;cargo;departamento\nJoão;joao@exemplo.com;desenvolvedor;TI"
                    )}>Clique aqui</span>
                    {" "} para baixar o padrão de CSV do E2W.
                  </p>
                  <p className="error-message">{error && "Ops! Esse não parece ser um arquivo .csv"}</p>
                  <DragAndDropUpload>
                    <UploadIcon />
                    {!file?.name ? <p className="describe-drag">arraste um arquivo csv para a tela ou</p> : <p>{file.name}</p>}
                    <UploadButton handleFileUpload={handleFileUpload} accept=".csv" />
                  </DragAndDropUpload>
                  <p className="info-describe">
                    Todos nessa lista receberão um convite via e-mail <br />para participar da comunidade.
                  </p>
                </>}
              {employee &&
                <>
                  <h3>Qual será a área e o cargo de acesso?</h3>
                  <div className="inputs-box">
                    <div className="custom-select-container next-button">
                      <div className="select-wrapper">
                        <select
                          className="custom-select"
                          value={selectedArea}
                          onChange={handleChangeUnit}
                          disabled={isStructureLoading}
                        >
                          <option value="" disabled>
                            Selecione a área
                          </option>
                          {units?.map((area, index) => (
                            <option key={index} value={area}>
                              {area}
                            </option>
                          ))}
                        </select>
                        <span className="custom-icon">
                          <span className="weui--arrow-outlined" />
                        </span>
                      </div>
                      <button className="add-button" type="button" onClick={() => setIsModalOpenUnit(true)}>
                        +
                      </button>
                      {errors?.selectedArea && <span className="error">{errors.selectedArea}</span>}
                    </div>
                    <div className="custom-select-container next-button">
                      <div className="select-wrapper">
                        <select
                          className="custom-select"
                          value={selectedRole}
                          onChange={handleChangeRole}
                          disabled={isStructureLoading || !selectedArea}
                        >
                          <option value="" disabled>
                            Selecione o cargo
                          </option>
                          {roles?.map((role, index) => (
                            <option key={index} value={role}>
                              {role}
                            </option>
                          ))}
                        </select>
                        <span className="custom-icon">
                          <span className="weui--arrow-outlined" />
                        </span>
                      </div>
                      <button className="add-button" type="button" onClick={() => setIsModalOpenRole(true)}>
                        +
                      </button>
                      {errors?.selectedRole && <span className="error">{errors.selectedRole}</span>}
                    </div>

                    <div className="custom-select-container next-button gestor-manager-container">
                      <div className="select-wrapper gestor-manager-input" style={{ position: 'relative' }}>
                        <input
                          type="text"
                          id="manager"
                          ref={managerInputRef}
                          placeholder={selectedArea ? "Digite para buscar gestor..." : "Selecione uma área primeiro"}
                          value={selectedManager ? selectedManager.display : managerSearch}
                          onChange={e => {
                            setManagerSearch(e.target.value);
                            setShowManagerDropdown(true);
                            setSelectedManager(null);
                          }}
                          onFocus={() => selectedArea && setShowManagerDropdown(true)}
                          className="custom-select"
                          disabled={!selectedArea}
                          autoComplete="off"
                        />
                        <span className="custom-icon" style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)' }}>
                          <span className="weui--arrow-outlined" />
                        </span>
                        <label htmlFor="manager" className="gestor-label">Gestor Responsável</label>
                        {showManagerDropdown && selectedArea && (
                          <div
                            className="manager-dropdown"
                            ref={managerDropdownRef}
                          >
                            {(() => {
                              if (isManagerLoading && !selectedManager) {
                                return <div className="manager-empty">Buscando gestores...</div>;
                              }
                              const filtered = Array.isArray(managers?.data?.items)
                                ? managers.data.items.filter(item => {
                                  const name = item?.manager?.name;
                                  const email = item?.manager?.email;
                                  const display = name && email ? `${name} - ${email}` : name || email || item?.manager?.email || String(item?.id);
                                  return display && (managerSearch === '' || display.toLowerCase().includes(managerSearch.toLowerCase()));
                                })
                                : [];
                              if (filtered.length === 0) {
                                return <div className="manager-empty">Nenhum gestor encontrado</div>;
                              }
                              return filtered.map(item => {
                                const name = item?.manager?.name;
                                const email = item?.manager?.email;
                                const display = name && email ? `${name} - ${email}` : name || email || item?.manager?.email || String(item?.id);
                                const isSelected = selectedManager?.id === item.manager?.id;
                                return (
                                  <div
                                    key={item.id || item.manager?.email || name}
                                    onClick={() => {
                                      handleManagerInputChange(display, item.manager.id);
                                    }}
                                    className={`manager-option improved ${isSelected ? 'selected' : ''}`}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 8,
                                      padding: '12px 18px',
                                      borderBottom: '1px solid #232a4d',
                                      fontSize: 15,
                                      background: isSelected ? '#232a4d' : 'none',
                                      color: isSelected ? '#ccc' : '#fff',
                                      cursor: isSelected ? 'default' : 'pointer'
                                    }}
                                  >
                                    <span style={{ fontWeight: 500 }}>{display}</span>
                                    {isSelected && <span style={{ marginLeft: 'auto', fontSize: 12 }}>✓</span>}
                                  </div>
                                );
                              });
                            })()}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                </>
              }
            </div>
          </div>
          <div className="modal-action-bar">
            <div className="modal-action-bar__content">
              <button type="submit" className="submit-button" disabled={isLoading}>
                {isLoading ? 'Salvando...' : employee ? 'SALVAR' : 'ENVIAR CONVITE'}
                {!isLoading && <span className="fa6-solid--arrow-right" />}
              </button>
            </div>
          </div>
        </form>
      </div>
      {popUpActive && <PopUpCheck
        buttonAction={() => {
          setPopUpActive(false)
          onClose();
        }}
        active={popUpActive}
        children={<p>{initialInfo.error ? 'Você não editou nenhuma informação!' : employee ? 'EDITADO COM SUCESSO!' : 'CONVITE ENVIADO COM SUCESSO!'}</p>}
      />}
      <SaveModal
        isOpen={isModalOpenUnit}
        onClose={() => setIsModalOpenUnit(false)}
        onSave={handleAddUnit}
      />
      <SaveModal
        isOpen={isModalOpenRole}
        onClose={() => setIsModalOpenRole(false)}
        onSave={handleAddRole}
        title="Adicionar novo cargo"
      />
    </div>,
    document.body
  );
};

export default InviteModalCollaborators;
